/**
 * GraphQL Authentication Service
 * Provides authentication operations using the .NET GraphQL API.
 *
 * Logging strategy:
 * - INFO: Login/register/logout success events (audit trail)
 * - WARN: Failed authentication attempts
 * - ERROR: Unexpected exceptions during auth operations
 * - DEBUG: Token refresh, session checks
 */

import logger from '../logger';
import {
  graphqlClient,
  extractData,
  setAuthToken,
  setRefreshToken,
  clearAuthTokens,
  getAuthToken,
} from './client';
import {
  LOGIN_MUTATION,
  REGISTER_MUTATION,
  REFRESH_TOKEN_MUTATION,
  ME_QUERY,
  CHANGE_PASSWORD_MUTATION,
  REQUEST_PASSWORD_RESET_MUTATION,
  RESET_PASSWORD_MUTATION,
  REQUEST_MAGIC_LINK_MUTATION,
  VERIFY_MAGIC_LINK_MUTATION,
} from './queries';
import type {
  GraphQLUser,
  AuthPayload,
  LoginInput,
  RegisterInput,
  LoginResponse,
  RegisterResponse,
  RefreshTokenResponse,
  MeResponse,
} from './types';
import { clearUserProfileCache } from './userService';

// ============================================
// User Interface for Frontend Compatibility
// ============================================

/**
 * User interface for frontend authentication
 */
export interface User {
  $id: string;
  $createdAt: string;
  $updatedAt: string;
  name: string;
  email: string;
  phone: string;
  emailVerification: boolean;
  phoneVerification: boolean;
  prefs: Record<string, unknown>;
  labels: string[];
  status: boolean;
}

/**
 * Convert GraphQL user to frontend User format
 */
function toUser(gqlUser: GraphQLUser): User {
  return {
    $id: gqlUser.id,
    $createdAt: gqlUser.createdAt,
    $updatedAt: gqlUser.updatedAt,
    name: gqlUser.fullName || '',
    email: gqlUser.email,
    phone: gqlUser.phoneNumber || '',
    emailVerification: true, // GraphQL API doesn't have email verification yet
    phoneVerification: false,
    prefs: {},
    labels: [gqlUser.role.toLowerCase()],
    status: gqlUser.isActive,
  };
}

// ============================================
// Authentication State
// ============================================

let currentUser: User | null = null;
let authStateListeners: ((user: User | null) => void)[] = [];

/**
 * Subscribe to auth state changes
 */
export function subscribeToAuthState(
  listener: (user: User | null) => void
): () => void {
  authStateListeners.push(listener);
  // Immediately notify with current state
  listener(currentUser);

  // Return unsubscribe function
  return () => {
    authStateListeners = authStateListeners.filter((l) => l !== listener);
  };
}

/**
 * Notify all listeners of auth state change
 */
function notifyAuthStateChange(user: User | null): void {
  currentUser = user;
  authStateListeners.forEach((listener) => listener(user));
}

// ============================================
// Authentication Functions
// ============================================

/**
 * Login with email and password
 */
export async function login(
  email: string,
  password: string
): Promise<{ user: User | null; error: Error | null }> {
  try {
    const input: LoginInput = { email, password };

    const response = await graphqlClient.mutate<LoginResponse>(LOGIN_MUTATION, {
      input,
    });

    const { data, error } = extractData(response);

    if (error || !data?.login) {
      return {
        user: null,
        error: new Error(error || 'Login failed'),
      };
    }

    const { token, refreshToken, user: gqlUser } = data.login;

    // Store tokens
    setAuthToken(token);
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }

    // Convert and store user
    const user = toUser(gqlUser);
    notifyAuthStateChange(user);

    // INFO: Successful login - audit event
    logger.info('Login successful', { email: user.email, userId: user.$id, role: user.labels[0] });
    return { user, error: null };
  } catch (error) {
    // ERROR: Unexpected exception during login
    logger.error('Login failed with exception', error as Error, { email });
    return { user: null, error: error as Error };
  }
}

/**
 * Register a new user
 */
export async function register(
  email: string,
  password: string,
  fullName: string,
  phoneNumber?: string
): Promise<{ user: User | null; error: Error | null }> {
  try {
    const input: RegisterInput = {
      email,
      password,
      fullName,
      phoneNumber,
    };

    const response = await graphqlClient.mutate<RegisterResponse>(
      REGISTER_MUTATION,
      { input }
    );

    const { data, error } = extractData(response);

    if (error || !data?.register) {
      return {
        user: null,
        error: new Error(error || 'Registration failed'),
      };
    }

    const { token, refreshToken, user: gqlUser } = data.register;

    // Store tokens
    setAuthToken(token);
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }

    // Convert and store user
    const user = toUser(gqlUser);
    notifyAuthStateChange(user);

    // INFO: Successful registration - audit event
    logger.info('Registration successful', { email: user.email, userId: user.$id });
    return { user, error: null };
  } catch (error) {
    // ERROR: Unexpected exception during registration
    logger.error('Registration failed with exception', error as Error, { email });
    return { user: null, error: error as Error };
  }
}

/**
 * Logout the current user
 */
export async function logout(): Promise<void> {
  // INFO: User logout - audit event
  logger.info('User logout initiated');
  clearAuthTokens();
  clearUserProfileCache();
  notifyAuthStateChange(null);
  logger.info('User logged out successfully');
}

/**
 * Get the current authenticated user
 */
export async function getCurrentUser(): Promise<User | null> {
  const token = getAuthToken();

  if (!token) {
    return null;
  }

  // Return cached user if available
  if (currentUser) {
    return currentUser;
  }

  try {
    const response = await graphqlClient.authenticatedQuery<MeResponse>(ME_QUERY);
    const { data, error } = extractData(response);

    if (error || !data?.me) {
      // Token might be invalid, clear it
      clearAuthTokens();
      clearUserProfileCache();
      notifyAuthStateChange(null);
      return null;
    }

    const user = toUser(data.me);
    notifyAuthStateChange(user);
    return user;
  } catch (error) {
    // ERROR: Failed to fetch current user session
    logger.error('Error getting current user', error as Error);
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

/**
 * Refresh the authentication token
 */
export async function refreshAuthToken(): Promise<boolean> {
  const token = getAuthToken();
  const refreshToken = localStorage.getItem('graphql_refresh_token');

  if (!token || !refreshToken) {
    return false;
  }

  try {
    const response = await graphqlClient.mutate<RefreshTokenResponse>(
      REFRESH_TOKEN_MUTATION,
      { token, refreshToken }
    );

    const { data, error } = extractData(response);

    if (error || !data?.refreshToken) {
      clearAuthTokens();
      notifyAuthStateChange(null);
      return false;
    }

    setAuthToken(data.refreshToken.token);
    if (data.refreshToken.refreshToken) {
      setRefreshToken(data.refreshToken.refreshToken);
    }

    const user = toUser(data.refreshToken.user);
    notifyAuthStateChange(user);

    return true;
  } catch (error) {
    // ERROR: Token refresh failed
    logger.error('Token refresh failed', error as Error);
    clearAuthTokens();
    notifyAuthStateChange(null);
    return false;
  }
}

/**
 * Create a new email session (alias for login)
 */
export async function createEmailSession(
  email: string,
  password: string
): Promise<{ user: User | null; error: Error | null }> {
  return login(email, password);
}

/**
 * Delete the current session (alias for logout)
 */
export async function deleteCurrentSession(): Promise<void> {
  return logout();
}

/**
 * Create a new account (alias for register)
 */
export async function createAccount(
  email: string,
  password: string,
  name: string
): Promise<{ user: User | null; error: Error | null }> {
  return register(email, password, name);
}

// ============================================
// Password Recovery
// ============================================

/**
 * Request password recovery email
 */
export async function requestPasswordRecovery(
  email: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    logger.info('Password recovery requested', { email });

    const response = await graphqlClient.mutate<{
      requestPasswordReset: { success: boolean; message: string };
    }>(REQUEST_PASSWORD_RESET_MUTATION, { input: { email } });

    const { data, error } = extractData(response);

    if (error) {
      return { success: false, error: new Error(error) };
    }

    return { success: data?.requestPasswordReset?.success ?? false, error: null };
  } catch (error) {
    logger.error('Password recovery request failed', error as Error);
    return { success: false, error: error as Error };
  }
}

/**
 * Complete password recovery using a reset token
 */
export async function completePasswordRecovery(
  _userId: string,
  token: string,
  newPassword: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    logger.debug('Completing password recovery');

    const response = await graphqlClient.mutate<{
      resetPassword: { success: boolean; message: string };
    }>(RESET_PASSWORD_MUTATION, { input: { token, newPassword } });

    const { data, error } = extractData(response);

    if (error) {
      return { success: false, error: new Error(error) };
    }

    return { success: data?.resetPassword?.success ?? false, error: null };
  } catch (error) {
    logger.error('Password recovery completion failed', error as Error);
    return { success: false, error: error as Error };
  }
}

/**
 * Change password for the authenticated user
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const response = await graphqlClient.authenticatedMutate<{
      changePassword: { success: boolean; message: string };
    }>(CHANGE_PASSWORD_MUTATION, { input: { currentPassword, newPassword } });

    const { data, error } = extractData(response);

    if (error) {
      return { success: false, error: new Error(error) };
    }

    logger.info('Password changed successfully');
    return { success: data?.changePassword?.success ?? false, error: null };
  } catch (error) {
    logger.error('Change password failed', error as Error);
    return { success: false, error: error as Error };
  }
}

// ============================================
// Magic Link Authentication
// ============================================

/**
 * Request a magic link login email
 */
export async function requestMagicLink(
  email: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    logger.info('Magic link requested', { email });

    const response = await graphqlClient.mutate<{
      requestMagicLink: { success: boolean; message: string };
    }>(REQUEST_MAGIC_LINK_MUTATION, { input: { email } });

    const { data, error } = extractData(response);

    if (error) {
      return { success: false, error: new Error(error) };
    }

    return { success: data?.requestMagicLink?.success ?? false, error: null };
  } catch (error) {
    logger.error('Magic link request failed', error as Error);
    return { success: false, error: error as Error };
  }
}

/**
 * Verify a magic link token and log in
 */
export async function verifyMagicLink(
  token: string
): Promise<{ user: User | null; error: Error | null }> {
  try {
    const response = await graphqlClient.mutate<{
      verifyMagicLink: { token: string; refreshToken: string; user: GraphQLUser };
    }>(VERIFY_MAGIC_LINK_MUTATION, { input: { token } });

    const { data, error } = extractData(response);

    if (error || !data?.verifyMagicLink) {
      return { user: null, error: new Error(error || 'Magic link verification failed') };
    }

    const { token: authToken, refreshToken, user: gqlUser } = data.verifyMagicLink;

    setAuthToken(authToken);
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }

    const user = toUser(gqlUser);
    notifyAuthStateChange(user);

    logger.info('Magic link login successful', { email: user.email, userId: user.$id });
    return { user, error: null };
  } catch (error) {
    logger.error('Magic link verification failed', error as Error);
    return { user: null, error: error as Error };
  }
}

// ============================================
// Default Export
// ============================================

export default {
  login,
  register,
  logout,
  getCurrentUser,
  isAuthenticated,
  refreshAuthToken,
  subscribeToAuthState,
  createEmailSession,
  deleteCurrentSession,
  createAccount,
  requestPasswordRecovery,
  completePasswordRecovery,
  changePassword,
  requestMagicLink,
  verifyMagicLink,
};
